import { Link, router } from "@inertiajs/react"
import { Edit05, Phone } from "@untitledui/icons"
import { format } from "date-fns"
import { useState } from "react"

import { CALL_BLOCK } from "@/components/calls/copy"
import { InCallPanel } from "@/components/calls/in-call-panel"
import { Button } from "@/components/ui/button"
import { Notice } from "@/components/ui/notice"
import { Panel } from "@/components/ui/panel"
import type { ActiveCall } from "@/pages/admin/calls/types"
import { adminSchoolPath, newAdminSchoolCallLogPath } from "@/routes"

import type { AgendaSection, Calling, SchoolHeader, Sequence } from "../types"

import { BeforeYouDial } from "./before-you-dial"

const day = (iso: string) => format(new Date(iso), "d MMM")

/** Where the school is in its touch sequence, in one line: what the next call is and when it is due. */
const sequenceLine = (s: Sequence, of: number) => {
  if (s.parked_until && new Date(s.parked_until) > new Date())
    return `Parked until ${day(s.parked_until)}.`
  if (s.current_step === 0) return "First attempt."
  const from =
    s.next_step_on && new Date(s.next_step_on) > new Date()
      ? ` from ${day(s.next_step_on)}`
      : ""
  return `Next is attempt ${s.next_step} of ${of}${from}.`
}

/** The sequence's five steps, as the server counts them. */
const STEPS = 5

/*
 * The call block on the school card. One slot, three modes: the Call button
 * (available, or disabled with its reason line in the same position so the
 * card never reflows); the pre-dial expansion; and, once a call exists, the
 * in-call panel in place — same slot, no navigation. do_not_call is the only
 * caution-tier reason: a fact about consent, not a gap the coordinator can fix.
 * active_call also disables Log a call: one call, one log, one at a time.
 */
export function CallActionBlock({
  calling,
  school,
  sequence,
  agenda,
  activeCall,
}: {
  calling: Calling
  school: SchoolHeader
  sequence: Sequence
  agenda: AgendaSection[]
  activeCall: ActiveCall | null | undefined
}) {
  const [dialling, setDialling] = useState(false)
  const attempt = { step: sequence.next_step, of: STEPS }
  const contactLine = school.ask_for
    ? `${school.ask_for.name}${school.ask_for.title ? ` · ${school.ask_for.title}` : ""}`
    : null

  if (activeCall && !dialling) {
    return (
      <div className="flex flex-col gap-3">
        <InCallPanel call={activeCall} pollPath={adminSchoolPath(school.id)} />
        {activeCall.kind === "terminal" && activeCall.state !== "completed" && (
          <div className="flex flex-wrap gap-2">
            <Button
              size="md"
              color="secondary"
              iconLeading={Phone}
              isDisabled={!calling.available}
              onPress={() => setDialling(true)}
            >
              Try again
            </Button>
          </div>
        )}
      </div>
    )
  }

  if (dialling) {
    return (
      <BeforeYouDial
        schoolId={school.id}
        schoolName={school.name}
        dialLabel={calling.number_to_dial_label}
        stafferMobile={calling.staffer_mobile_masked}
        attempt={attempt}
        agenda={agenda}
        onBack={() => setDialling(false)}
      />
    )
  }

  const reasonLine = () => {
    switch (calling.reason) {
      case "no_staffer_mobile":
        return (
          <>
            Add your mobile number to{" "}
            <Link
              href="/settings/profile"
              className="text-brand-secondary hover:text-brand-secondary_hover"
            >
              your profile
            </Link>{" "}
            to call from here.
          </>
        )
      case "no_number":
        return CALL_BLOCK.no_number
      case "active_call":
        return (
          <>
            {CALL_BLOCK.active_call}{" "}
            {calling.active_call && (
              <Link
                href={adminSchoolPath(calling.active_call.school_id)}
                className="text-brand-secondary hover:text-brand-secondary_hover"
              >
                Go to your call — {calling.active_call.school_name}
              </Link>
            )}
          </>
        )
      case "outside_hours":
        return calling.hours ? (
          <>
            It is <span className="notation">{calling.hours.school_time}</span>{" "}
            at the school. Calling opens at{" "}
            <span className="notation">{calling.hours.opens_at}</span>.
          </>
        ) : (
          "Outside calling hours."
        )
      case "not_installed":
        return CALL_BLOCK.not_installed
      default:
        return null
    }
  }

  return (
    <Panel title="Call this school" note={contactLine} headingLevel="h3">
      {calling.reason === "do_not_call" ? (
        <Notice tone="caution" role="status">
          {CALL_BLOCK.do_not_call}
        </Notice>
      ) : (
        <p className="text-tertiary text-sm">
          {calling.available ? (
            <>
              Would dial{" "}
              <span className="notation text-primary">
                {calling.number_to_dial_label}
              </span>
              . {sequenceLine(sequence, STEPS)}
            </>
          ) : (
            reasonLine()
          )}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          color="primary"
          size="md"
          iconLeading={Phone}
          isDisabled={!calling.available}
          onPress={() => setDialling(true)}
        >
          Call
        </Button>
        {calling.log_call_available && (
          <Button
            color="secondary"
            size="md"
            iconLeading={Edit05}
            isDisabled={calling.reason === "active_call"}
            onPress={() => router.visit(newAdminSchoolCallLogPath(school.id))}
          >
            Log a call
          </Button>
        )}
      </div>
      {calling.log_call_available ? (
        <p className="text-tertiary text-xs">
          Rang them from your own phone? Log it here and the Task lands on the
          account in Salesforce.
        </p>
      ) : (
        <p className="text-tertiary text-xs">
          Sign in with Salesforce to log a call by hand.
        </p>
      )}
    </Panel>
  )
}
